import axios from 'axios';

import { CalendarEventDTO } from '../models/CalendarEventDTO';

import { isViewAs } from '../viewAs/viewAsSession';

import { healioSpaceAPIURL } from './baseHealioSpaceAPI';
import { installSessionInterceptors } from './sessionInterceptors';
export const calendarApi = axios.create({
  baseURL: `${healioSpaceAPIURL}/calendar`,
  withCredentials: true,
});

installSessionInterceptors(calendarApi);

export const getAllEvents = async () => {
  const cacheKey = 'calendarEvents';
  const cacheExpiryKey = 'calendarEventsExpiry';
  const cacheExpiryTimeInMilliseconds = 60 * 60 * 1000; // 60 minutes in milliseconds

  // HA-447: a support view-as tab keeps the viewed member's calendar out of localStorage, which
  // is shared with the admin's own tab and outlives the session.
  const useCache = !isViewAs();
  const cachedData = useCache ? localStorage.getItem(cacheKey) : null;
  const cachedExpiry = useCache ? localStorage.getItem(cacheExpiryKey) : null;

  if (cachedData && cachedExpiry) {
    const expiryTime = parseInt(cachedExpiry, 10);

    if (Date.now() < expiryTime) {
      return JSON.parse(cachedData) as CalendarEventDTO[];
    }
  }

  try {
    const response = await calendarApi.get('');
    const data = response.data as CalendarEventDTO[];

    if (useCache) {
      localStorage.setItem(cacheKey, JSON.stringify(data));
      localStorage.setItem(cacheExpiryKey, (Date.now() + cacheExpiryTimeInMilliseconds).toString());
    }

    return data;
  } catch (error) {
    console.error('Error fetching data:', error);
  }
};

export const getEvent = async (eventId: string) => {
  try {
    const response = await calendarApi.get(`/${encodeURIComponent(eventId)}`);

    return response.data as CalendarEventDTO;
  } catch (error) {
    console.error('Error fetching event:', error);
  }
};

export const addEvent = async (calendarEvent: CalendarEventDTO) => {
  try {
    const response = await calendarApi.post('', calendarEvent);

    return response.data as CalendarEventDTO;
  } catch (error) {
    console.error('Error adding event:', error);
  }
};

export const updateEvent = async (eventId: string, calendarEvent: CalendarEventDTO) => {
  try {
    await calendarApi.put(`/${encodeURIComponent(eventId)}`, calendarEvent);
  } catch (error) {
    console.error('Error updating event:', error);
  }
};

export const deleteEvent = async (eventId: string) => {
  try {
    await calendarApi.delete(`/${encodeURIComponent(eventId)}`);
  } catch (error) {
    console.error('Error deleting event:', error);
  }
};

/**
 * Get upcoming and previous appointments for a specific client (HA-165)
 * @param clientId The client ID to get appointments for
 * @returns Object with next and previous appointment info
 */
export const getClientAppointments = async (
  clientId: string
): Promise<{
  next:
    | import('../components/clientManagement/interfaces/clientQuickViewProps').ClientAppointmentInfo
    | null;
  previous:
    | import('../components/clientManagement/interfaces/clientQuickViewProps').ClientAppointmentInfo
    | null;
}> => {
  try {
    const response = await calendarApi.get(`/client/${clientId}/appointments`);

    return response.data;
  } catch (error) {
    console.error('Error fetching client appointments:', error);

    // Return empty results on error - the hook will handle display
    return { next: null, previous: null };
  }
};
